import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { getDescendants, getRootItems, hasChildrenInArray, sortByKey } from '../utils/common';
import { TreeEventType } from '../utils/enums';

export interface TreeEvent {
  type: TreeEventType;
  data: any;
}

@Injectable({
  providedIn: 'root',
})
export class TreeItemService {
  private treeEvent = new Subject<TreeEvent>();
  treeEvent$ = this.treeEvent.asObservable();
  allTreeNodeItems = signal<Array<any>>([]);
  searchedText = signal<string>('');

  constructor() {}

  setTreeEvent(data: TreeEvent) {
    this.treeEvent.next(data);
  }

  /**
   * fetch item by id
   */
  getItemById(id: string) {
    return this.allTreeNodeItems().find((item) => item.id === id);
  }

  /**
   * Fetch children data by parent id
   */
  getChildren(parentId: string) {
    return this.allTreeNodeItems().filter((n) => n.parent_id === parentId);
  }

  /**
   * convert json data to tree structure data
   */
  convertToTreeStructure(arrayOfItems: any[]): any[] {
    return this.convertToTreeStructureHelper(arrayOfItems, null);
  }

  private convertToTreeStructureHelper(
    arrayOfItems: any[],
    parentId: number
  ): any[] {
    const output: Array<any> = [];

    // get all those items that don't have a parent within the array
    let rootItems = getRootItems(arrayOfItems);
    rootItems = sortByKey(rootItems, 'display_order'); // sort root items by display order

    for (const thisRootItem of rootItems) {
      // this node's parent isn't in this tree but is in the main dashboard tree, this means the node is a root node in trash and therefore should retain original parent id value in case it's moved out of trash back into the dashboard (in which case we want to move it back to its original position under the same parent if possible)
      if (!parentId && thisRootItem['parent_id']) {
        if (this.getItemById(thisRootItem['parent_id'])) {
          parentId = thisRootItem['parent_id'];
        }
      }

      // convert datetimes within item history records to proper javascript format
      const historyDeepCopy =
        thisRootItem['history']?.map((record) => ({
          ...record,
          timestamp: new Date(record.timestamp),
        })) || [];

      // and sort records by most recent first
      historyDeepCopy.sort((a, b) => b.timestamp - a.timestamp);
      const reformattedItem = {
        ...thisRootItem,
        parent_id: parentId,
        subitems_expanded: false,
        history: historyDeepCopy,
      };
      if (!hasChildrenInArray(thisRootItem, arrayOfItems)) {
        reformattedItem['hasChildren'] = false;
        reformattedItem['children'] = [];
        output.push(reformattedItem);
      } else {
        reformattedItem['hasChildren'] = true;
        reformattedItem['children'] = this.convertToTreeStructureHelper(
          getDescendants(thisRootItem, arrayOfItems),
          reformattedItem['id']
        );
        output.push(reformattedItem);
      }
    }
    return output;
  }
}
